/**
 * Lead Agent Node Component
 *
 * The supervisor/lead agent node for the demo workflow diagram.
 * Displays as a rectangular card matching the Command Center style.
 *
 * @module components/home/multi-agent/nodes/lead-agent-node
 */

import { memo } from "react"
import { Handle, Position } from "@xyflow/react"
import type { NodeProps } from "@xyflow/react"
import { AgentIcon } from "@/components/agents/agent-icon"

/**
 * Data for lead agent node.
 */
export interface LeadAgentNodeData {
  name: string
  agentCount: number
}

/**
 * LeadAgentNode renders the supervisor card in the demo diagram.
 */
function LeadAgentNodeComponent({ data }: NodeProps) {
  const { name, agentCount } = data as LeadAgentNodeData

  return (
    <div className="relative">
      {/* "Lead Agent" label above card */}
      <div className="absolute -top-6 left-0 right-0 flex justify-center">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
          Lead Agent
        </span>
      </div>

      {/* Main card */}
      <div className="relative w-[200px] rounded-lg border border-neutral-600 bg-neutral-700 px-4 py-3 shadow-lg">
        {/* Content */}
        <div className="flex items-center gap-3">
          {/* Agent profile icon */}
          <AgentIcon name={name} size="lg" rounded="lg" />

          {/* Text */}
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-white">
              {name}
            </h3>
            <p className="truncate text-xs text-neutral-300">
              Orchestrates {agentCount} agent{agentCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Connection handle on the right */}
        <Handle
          type="source"
          position={Position.Right}
          className="!h-3 !w-3 !border-2 !border-white !bg-primary"
        />
      </div>
    </div>
  )
}

export const LeadAgentNode = memo(LeadAgentNodeComponent)
